import botIcon1 from './assets/bot-icons/bot-icon-1.png';
import aiAssistantConstants from './Constants.AiAssistant';

export interface AppSpecificConstants {
    app: {
        name: string;
    };
    bot: {
        profile: {
            fullName: string;
        };
    };

    [key: string]: unknown;
}

const moreConstants: AppSpecificConstants = aiAssistantConstants;

const commonConstants = {
    app: {
        name: 'AI Assistant',
        updateCheckIntervalSeconds: 60 * 5,
        CONNECTION_ALERT_ID: 'connection-alert',
    },
    bot: {
        profile: {
            id: 'bot',
            fullName: 'AI Assistant',
            emailAddress: '',
            photo: botIcon1,
        },
        fileExtension: 'skcb',
        typingIndicatorTimeoutMs: 5000,
    },
    sk: {
        service: {
            defaultDefinition: 'int',
        },
        // Reserved context variable names
        reservedWords: ['server_url', 'server-url'],
        // Flag used to indicate that the variable is unknown in plan preview
        UNKNOWN_VARIABLE_FLAG: '$???',
    },
    adoScopes: ['vso.work'],
    BATCH_REQUEST_LIMIT: 20,
    // UI mirror of the server's ChatHistoryExportController.MaxChatsPerExport (webapi). The server is
    // the source of truth — it enforces the cap and returns it in the 400 body — but the UI needs the
    // number to cap a "select all" and to warn before firing a doomed request. If the two ever drift,
    // the over-cap message still shows the server's value (read from the 400 body).
    MAX_CHATS_PER_EXPORT: 500,
    plugins: {
        // For a list of Microsoft Graph permissions, see https://learn.microsoft.com/en-us/graph/permissions-reference.
        // Your application registration will need to be granted these permissions in Azure Active Directory.
        msGraphScopes: ['Calendars.Read', 'Mail.Read', 'Mail.Send', 'Tasks.ReadWrite', 'User.Read'],
        // All OpenAI plugin manifest files should be located at this path per OpenAI requirements: "https://platform.openai.com/docs/plugins/getting-started/plugin-manifest
        MANIFEST_PATH: '/.well-known/ai-plugin.json',
    },
    liveChat: {
        // Typing-indicator timings (#1770). Sender (customer ChatInput): emit a leading "true"
        // throttled to at most once per TYPING_THROTTLE_MS while typing, and a "false" after
        // TYPING_INACTIVITY_MS of no keystroke (or immediately on send). Receiver (ChatStatus):
        // clear the indicator after TYPING_RECEIVER_TIMEOUT_MS without a fresh ping — a backstop
        // for a lost "stopped" signal. The receiver timeout must exceed the sender throttle so
        // continuous typing keeps the indicator alive between re-emitted "true" pings.
        TYPING_THROTTLE_MS: 2000,
        TYPING_INACTIVITY_MS: 3000,
        TYPING_RECEIVER_TIMEOUT_MS: 5000,
    },
    KEYSTROKE_DEBOUNCE_TIME_MS: 250,
    // Cooldown after a Stop-click before Send is re-enabled. 500 ms is a pragmatic
    // balance: long enough for the cancel POST to reach the server and tear down
    // the workflow before the next Send fires (avoiding the workflow-wins-the-race
    // scenario from #1554), short enough that users on fast networks don't
    // perceive lag. Not exposed as a runtime setting because the race window is
    // network-latency-bound, not user-tunable.
    CANCEL_COOLDOWN_MS: 500,
};

function isObject(item: unknown): item is Record<string, unknown> {
    return Boolean(item && typeof item === 'object' && !Array.isArray(item));
}

function deepMerge<T extends Record<string, unknown>, U extends Record<string, unknown>>(
    target: T,
    source: U | null,
): T & U {
    if (source === null) {
        return target as T & U;
    }
    const output: Record<string, unknown> = { ...target, ...source };

    Object.keys(source).forEach((key) => {
        if (isObject(target[key]) && isObject(source[key])) {
            output[key] = deepMerge(target[key], source[key]);
        } else {
            output[key] = source[key];
        }
    });

    return output as T & U;
}

export const Constants = deepMerge(
    commonConstants as Record<string, unknown>,
    moreConstants,
) as typeof commonConstants & typeof moreConstants;
